import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Send, Sparkles } from "lucide-react";
import { coachChat, listThreads, getThread } from "@/lib/weave";

const STARTER_PROMPTS = [
  "Help me write my first post this week.",
  "What pillar should I lean into for credibility?",
  "Critique this draft: …",
  "I'm stuck. What would a Weave mentor ask me?",
];

export default function Coach() {
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null); // {thread_id, messages, title}
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const reloadThreads = async () => {
    try {
      const t = await listThreads();
      setThreads(t);
    } catch {}
  };
  useEffect(() => {
    reloadThreads();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [activeThread]);

  const openThread = async (id) => {
    try {
      const t = await getThread(id);
      setActiveThread(t);
    } catch {
      toast.error("Couldn't open thread.");
    }
  };

  const newThread = () => setActiveThread({ thread_id: null, messages: [], title: "New conversation" });

  const send = async (text) => {
    const msg = (text ?? draft).trim();
    if (!msg) return;
    setSending(true);
    const optimisticUser = { role: "user", content: msg, ts: new Date().toISOString() };
    setActiveThread((t) => ({
      ...(t || { thread_id: null, messages: [], title: msg.slice(0, 60) }),
      messages: [...((t?.messages) || []), optimisticUser],
    }));
    setDraft("");
    try {
      const res = await coachChat(activeThread?.thread_id || null, msg);
      setActiveThread((t) => ({
        thread_id: res.thread_id,
        title: res.title || t?.title || msg.slice(0, 60),
        messages: [
          ...((t?.messages) || []),
          { role: "assistant", content: res.reply, ts: new Date().toISOString() },
        ],
      }));
      reloadThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Coach is unavailable.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="coach-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Module 08</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
            Your <span className="text-[var(--teal)]">Personal Branding Coach.</span>
          </h1>
          <p className="mt-3 text-[var(--ink-2)] max-w-2xl">
            Trained in The Weave's voice. Draws on your foundations and pillars. Always the same
            tone: warm, grounded, useful.
          </p>
        </div>
        <Button onClick={newThread} className="btn-primary rounded-full h-11 px-5 flex items-center gap-2" data-testid="new-thread-btn">
          <Plus size={16} /> New conversation
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* threads */}
        <aside className="md:col-span-4 lg:col-span-3 card-tech rounded-sm p-4 max-h-[70vh] overflow-y-auto">
          <div className="overline mb-3">Conversations</div>
          {threads.length === 0 ? (
            <p className="text-sm text-[var(--muted)]">No conversations yet.</p>
          ) : (
            <ul className="space-y-1">
              {threads.map((t) => (
                <li key={t.thread_id}>
                  <button
                    onClick={() => openThread(t.thread_id)}
                    className={`w-full text-left p-2 rounded-sm hover:bg-[var(--bg-2)] ${
                      activeThread?.thread_id === t.thread_id ? "bg-[var(--bg-2)]" : ""
                    }`}
                    data-testid={`thread-${t.thread_id}`}
                  >
                    <div className="text-sm font-display tracking-tight line-clamp-2">
                      {t.title || "Untitled"}
                    </div>
                    <div className="text-[10px] text-[var(--muted)] font-mono-mark mt-0.5">
                      {(t.updated_at || "").slice(0, 10)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* chat */}
        <section className="md:col-span-8 lg:col-span-9 card-tech rounded-sm flex flex-col h-[70vh]">
          {!activeThread ? (
            <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
              <Sparkles size={32} className="text-[var(--teal)]" />
              <h3 className="font-display text-2xl tracking-tight mt-4">Say hi to your coach.</h3>
              <p className="text-[var(--ink-2)] mt-2 max-w-md">
                Ask anything about your positioning, content, or what to post next.
              </p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-xl w-full">
                {STARTER_PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => {
                      setActiveThread({ thread_id: null, messages: [], title: p.slice(0, 60) });
                      send(p);
                    }}
                    className="text-left text-sm p-3 border border-[var(--line)] rounded-sm hover:bg-[var(--bg-2)]"
                    data-testid="starter-prompt"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-scroll">
                {(activeThread.messages || []).map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-sm p-4 ${
                        m.role === "user"
                          ? "bg-[var(--ink)] text-white"
                          : "bg-[var(--bg-2)] text-[var(--ink)] border border-[var(--line)]"
                      }`}
                      data-testid={`msg-${i}`}
                    >
                      <p className="whitespace-pre-line text-sm leading-relaxed">{m.content}</p>
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-[var(--bg-2)] border border-[var(--line)] rounded-sm p-4">
                      <span className="font-mono-mark text-xs text-[var(--muted)] blink">coach is thinking…</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="border-t border-[var(--line)] p-4 flex items-end gap-3">
                <Textarea
                  rows={2}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Type a message…  (⌘/Ctrl + Enter to send)"
                  className="rounded-sm border-[var(--line)] flex-1"
                  data-testid="coach-input"
                />
                <Button
                  onClick={() => send()}
                  disabled={sending || !draft.trim()}
                  className="btn-accent rounded-full h-11 px-5 flex items-center gap-2"
                  data-testid="coach-send-btn"
                >
                  <Send size={14} /> Send
                </Button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
